import { createContext, useContext, useState, useCallback } from 'react'

import type { ReactNode } from 'react'

interface BrandColor {
  hex: string
  name: string
  usage: string
}

interface WordCloudItem {
  word: string
  weight: number
}

interface Competitor {
  name: string
  sentiment: number
}

interface AudienceDemographics {
  ageGroups: { range: string; percentage: number }[]
  topRegions: { name: string; percentage: number }[] | string[]
  genderSplit?: { male: number; female: number; other: number }
}

export interface BrandData {
  name: string
  colors: BrandColor[]
  fonts: string[]
  visualStyle: string
  logo: string | null
  summary: string
  keywords: string[]
  designDirective?: string
}

export interface SentimentData {
  overall: number
  positive: number
  negative: number
  neutral: number
  trendingTopics: string[]
  wordCloud: WordCloudItem[]
  audienceDemographics: AudienceDemographics
  competitors: Competitor[]
  suggestedRetailEvents?: string[]
}

export interface BrandAnalysisResponse {
  brand: BrandData
  sentiment: SentimentData
  recommendedStyles: string[]
}

export type CreativityLevel = 'protected' | 'balanced' | 'wild'

export interface GraphicItem {
  type: 'uploaded'
  url: string
  name: string
  file?: File
}

interface BrandContextType {
  brandAnalysis: BrandAnalysisResponse | null
  setBrandAnalysis: (data: BrandAnalysisResponse) => void
  selectedGraphics: GraphicItem[]
  setSelectedGraphics: (graphics: GraphicItem[]) => void
  designPrompt: string
  setDesignPrompt: (prompt: string) => void
  creativityLevel: CreativityLevel
  setCreativityLevel: (level: CreativityLevel) => void
  selectedRetailEvent: string | null
  setSelectedRetailEvent: (event: string | null) => void
  clearSession: () => void
}

const BrandContext = createContext<BrandContextType | null>(null)

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brandAnalysis, setBrandAnalysisState] =
    useState<BrandAnalysisResponse | null>(null)
  const [selectedGraphics, setSelectedGraphics] = useState<GraphicItem[]>([])
  const [designPrompt, setDesignPrompt] = useState('')
  const [creativityLevel, setCreativityLevel] = useState<CreativityLevel>('protected')
  const [selectedRetailEvent, setSelectedRetailEvent] = useState<string | null>(null)

  const setBrandAnalysis = useCallback((data: BrandAnalysisResponse) => {
    setBrandAnalysisState(data)
  }, [])

  const clearSession = useCallback(() => {
    setBrandAnalysisState(null)
    setSelectedGraphics([])
    setDesignPrompt('')
    setCreativityLevel('protected')
    setSelectedRetailEvent(null)
  }, [])

  return (
    <BrandContext.Provider
      value={{
        brandAnalysis,
        setBrandAnalysis,
        selectedGraphics,
        setSelectedGraphics,
        designPrompt,
        setDesignPrompt,
        creativityLevel,
        setCreativityLevel,
        selectedRetailEvent,
        setSelectedRetailEvent,
        clearSession
      }}
    >
      {children}
    </BrandContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBrandContext(): BrandContextType {
  const context = useContext(BrandContext)
  if (!context) {
    throw new Error('useBrandContext must be used within a BrandProvider')
  }
  return context
}
